import * as fromSearch from './search/search.reducers';
import * as fromOffer from './offer/offer.reducers';
import * as fromSalesOffer from './sales-offer/sales-offer.reducers';
import * as fromEnforcedAuctionOffer from './enforced-auction-offer/enforced-auction-offer.reducers';
import * as fromSales from './sales/sales.reducers';
import * as fromUser from './user/user.reducers';
import * as fromUserSettings from './user/user-settings/user-settings.reducers';
import * as fromVehicle from './vehicle-maintenance/vehicle-maintenance.reducers';
import * as fromDetailPage from './detail-page/detail-page.reducers';
import * as fromSpinner from './spinner/spinner.reducers';
import * as fromNotification from './notification/notification.reducers';
import * as fromOfferMaintenance from './maintenance-offers/maintenance-offer.reducer';
import {ActionReducerMap} from '@ngrx/store';

// This is the main redux store, the aggregation of all sub-stores

export interface AppState {
  offerMaintenanceState: fromOfferMaintenance.State;
  searchState: fromSearch.State;
  offerState: fromOffer.StateOffer;
  salesOfferState: fromSalesOffer.StateSalesOffer;
  sales: fromSales.State;
  enforcedAuctionOfferState: fromEnforcedAuctionOffer.StateEnforcedAuctionOffer;
  userState: fromUser.State;
  userSettingsState: fromUserSettings.State;
  vehicleMaintenanceState: fromVehicle.State;
  detailPageState: fromDetailPage.State;
  spinnerState: fromSpinner.State;
  notificationState: fromNotification.State;
}

/**
 * The app store aggregates all sub-state reducers
 */
export const reducers: ActionReducerMap<AppState> = {
  searchState: fromSearch.searchReducer,
  offerState: fromOffer.offerReducer,
  salesOfferState: fromSalesOffer.salesOfferReducer,
  sales: fromSales.salesReducer,
  enforcedAuctionOfferState: fromEnforcedAuctionOffer.enforcedAuctionOfferReducer,
  userState: fromUser.userReducer,
  userSettingsState: fromUserSettings.userSettingsReducer,
  vehicleMaintenanceState: fromVehicle.vehicleReducer,
  detailPageState: fromDetailPage.detailPageReducer,
  spinnerState: fromSpinner.spinnerReducer,
  notificationState: fromNotification.notificationReducer,
  offerMaintenanceState: fromOfferMaintenance.maintenanceOfferReducer
};

/**
 * Expose our read-only (sub-)states to the application as needed
 */
// sales search state
export const getSearchState = (state: AppState) => state.searchState;
export const getSearchAggregation = (state: AppState) => state.searchState.searchAggregation;
export const getSearchParams = (state: AppState) => state.searchState.searchParams;
export const getFinishedSearchParams = (state: AppState) => state.searchState.finishedSearchParams;
export const getOfferMaintenanceSearchParams = (state: AppState) => state.offerMaintenanceState.searchParams;
export const getOfferMaintenancePageSettings = (state: AppState) => state.offerMaintenanceState.pageSettings;
export const getMaintenanceOffers = (state: AppState) => state.offerMaintenanceState.offers;
export const getEnforcedAuctionOfferState = (state: AppState) => state.enforcedAuctionOfferState;
export const getEnforcedAuctionOffers = (state: AppState) => state.enforcedAuctionOfferState.offers;
export const getEnforcedAuctionOfferPageSettings = (state: AppState) => state.enforcedAuctionOfferState.pageSettings;
export const getEnforcedAuctionOfferSearchAggregation = (state: AppState) => state.enforcedAuctionOfferState.searchAggregation;
export const getEnforcedAuctionOfferSearchParams = (state: AppState) => state.enforcedAuctionOfferState.searchParams;
// offer state
export const getOffer = (state: AppState) => state.offerState.offer;
export const getOffers = (state: AppState) => state.salesOfferState.offers;
export const getBookmarkedOffersPIA = (state: AppState) => state.offerState.bookmarkedOffersPIA;
export const getBookmarkedOffersPB = (state: AppState) => state.offerState.bookmarkedOffersPB;
export const getBookmarkedOffersALLUC = (state: AppState) => state.offerState.bookmarkedOffersALLUC;
export const getBookmarkedOffersDINBIL = (state: AppState) => state.offerState.bookmarkedOffersDINBIL;
export const getBookmarkedOffersVGRX = (state: AppState) => state.offerState.bookmarkedOffersVGRX;
export const getSalesScope = (state: AppState) => state.sales.salesScope;
export const getCurrentUserCompanyOffersPIA = (state: AppState) => state.offerState.currentUserCompanyOffersPIA;
export const getCurrentUserCompanyOffersPB = (state: AppState) => state.offerState.currentUserCompanyOffersPB;
export const getCurrentUserCompanyOffersALLUC = (state: AppState) => state.offerState.currentUserCompanyOffersALLUC;
export const getCurrentUserCompanyOffersDINBIL = (state: AppState) => state.offerState.currentUserCompanyOffersDINBIL;
export const getCurrentUserCompanyOffersVGRX = (state: AppState) => state.offerState.currentUserCompanyOffersVGRX;
export const getFinishedUserCompanyOffersPIA = (state: AppState) => state.offerState.finishedUserCompanyOffersPIA;
export const getFinishedUserCompanyOffersPB = (state: AppState) => state.offerState.finishedUserCompanyOffersPB;
export const getFinishedUserCompanyOffersALLUC = (state: AppState) => state.offerState.finishedUserCompanyOffersALLUC;
export const getFinishedUserCompanyOffersDINBIL = (state: AppState) => state.offerState.finishedUserCompanyOffersDINBIL;
export const getFinishedUserCompanyOffersVGRX = (state: AppState) => state.offerState.finishedUserCompanyOffersVGRX;
export const getViewSettings = (state: AppState) => state.offerState.settings;
export const getPageSettings = (state: AppState) => state.offerState.pageSettings;
export const getPageSettingsDistributionChannel = (state: AppState) => state.offerState.pageSettings.channel;
export const getSalesPageSettings = (state: AppState) => state.salesOfferState.pageSettings;
export const getAuctionBid = (state: AppState) => state.offerState.auctionBid;
// vehicle maintenance state
export const getMaintenanceScope = (state: AppState) => state.vehicleMaintenanceState.maintenanceScope;
export const getVehicleMaintenanceState = (state: AppState) => state.vehicleMaintenanceState;
export const getVehicleMaintenanceDistributionChannel = (state: AppState) => state.vehicleMaintenanceState.pageSettings.channel;
export const getMaintenanceVehicle = (state: AppState) => state.vehicleMaintenanceState.vehicle;
export const getMaintenanceVehicles = (state: AppState) => state.vehicleMaintenanceState.vehicles;
export const getMaintenanceVehicleBasket = (state: AppState) => state.vehicleMaintenanceState.vehiclesBasket;
export const getVehicleMaintenanceSearchPageSettings = (state: AppState) => state.vehicleMaintenanceState.pageSettings;
export const getVehicleMaintenanceActiveCategory = (state: AppState) => state.vehicleMaintenanceState.activeCategory;
export const getVehicleMaintenanceSearchFilterConfig = (state: AppState) => state.vehicleMaintenanceState.searchFilterConfig;
export const getOpenTasks = (state: AppState) => state.vehicleMaintenanceState.openTasks;
export const getEnforcedAuctionVehicles = (state: AppState) => state.vehicleMaintenanceState.vehicles;
// user state
export const getUserState = (state: AppState) => state.userState;
export const getUserLocal = (state: AppState) => state.userState.local;
export const getUserInfo = (state: AppState) => state.userState.userInfo;
export const getUserSettings = (state: AppState) => state.userSettingsState.userSettings;
// notification state
export const getUserNotifications = (state: AppState) => state.notificationState.userNotifications;
export const getChannelNotifications = (state: AppState) => state.notificationState.channelNotifications;
// detail state
export const getDetailPageBacklink = (state: AppState) => state.detailPageState.backlink;
// spinner state
export const getLoading = (state: AppState) => state.spinnerState.loading;

export const getUserSubstitutions = (state: AppState) => state.vehicleMaintenanceState.userSubstitutions;
